
import "tippy.js/dist/tippy.css";

import EventBus from "@/lib/event-bus";
import { setLanguage } from "@/lib/i18n";
import { firstAvailableDate, getSentry, getToday, hasProperty, parseDate, gettersInstance, isMobile, getFeatureData } from "./lib/util";
import { DEFAULT_RASTER_KIND, demoDefaultSensingDate, envConfig, RASTER_TYPES, useSentry } from "./lib/constants";
import ErrorPage from "@/views/App/ErrorPage.vue";
import HijackNotification from "@/components/HijackNotification.vue";
import DemoFarmModal from "@/components/Modals/DemoFarmModal.vue";
import PolicyUpdatePopup from "@/components/Popups/PolicyUpdatePopup.vue";
import CookieConsent from "@/components/Popups/CookieConsent.vue";
import PayWall from "@/components/PayWall/PayWall.vue";
import SearchSidebar from "@/components/Sidebar/SearchSidebar.vue";
import ChangeParcelCrop from "@/components/Parcel/ChangeParcelCrop.vue";
import JournalDialog from "./components/Journal/JournalDialog.vue";
import AppSidebarTransition from "@/components/Sidebar/AppSidebarTransition.vue";
import OgorNotifications from "@/components/OgorNotifications.vue";
import PwaInstallPrompt from "@/components/Modals/PwaInstallPrompt.vue";
import LastSeasonYieldNoticePrompt from "@/components/Modals/LastSeasonYieldNoticePrompt.vue";
import DowntimeNotice from "@/components/Landing/DowntimeNotice.vue";
import "vue-class-component/hooks";
import { Component, MixedVue, mixins } from "./vue";
import { Route } from "vue-router";
import { Watch } from "vue-property-decorator";
import { RasterKind } from "./lib/types/Raster";
import DialogManager from "./components/DialogManager.vue";
import SetCropsModal from "./components/Modals/SetCropsModal.vue";
import { getRouteMeta } from "./router";
import { Parcel } from "./lib/types/Parcel";

const StoreGetters = gettersInstance([
  "user",
  "userEmail",
  "userLastName",
  "userFirstName",
  "userLicense",
  "userLicenseIsTrial",
  "userLicenseIsActive",
  "userLatestLegalConsent",
  "userIsAuthenticated",
  "farms",
  "farmIds",
  "allFarms",
  "userOwnsFarm",
  "userHasAccessToFarm",
  "userHasFullAccessToFarm",
  "totalFarms",
  "currentFarm",
  "currentFarmId",
  "currentSeason",
  "currentSeasonId",
  "totalSeasons",
  "seasons",
  "availableDates",
  "selectedDate",
  "defaultUserFarm",
  "farmsWithParcels",
  "isDemoFarm",
  "demoFarmId",
  "defaultDemoSeasonId",
  "showSearchSidebar",
  "selectedParcelId",
]);

@Component({
  name: "App",
  components: {
    AppSidebarTransition,
    SearchSidebar,
    PayWall,
    DemoFarmModal,
    HijackNotification,
    ErrorPage,
    PolicyUpdatePopup,
    CookieConsent,
    ChangeParcelCrop,
    OgorNotifications,
    PwaInstallPrompt,
    LastSeasonYieldNoticePrompt,
    DowntimeNotice,
    DialogManager,
    JournalDialog,
    SetCropsModal,
  },
  head: {
    link() {
      return Object.keys(this.$language.available)
        .map((lang) => {
          return {
            rel: "alternate",
            hreflang: lang,
            href: `${envConfig.VUE_APP_HOST}/${lang}`,
            id: `hreflang-alternate-${lang}`,
          };
        })
        .concat([
          {
            rel: "alternate",
            hreflang: "x-default",
            href: envConfig.VUE_APP_HOST,
            id: "hreflang-alternate-default",
          },
        ]);
    },
    meta() {
      let seo: { title?: string; description?: string; image?: string } = {};
      if (this.$route.meta?.seo) {
        if (typeof this.$route.meta.seo == "function") seo = this.$route.meta.seo(this);
        else {
          seo = this.$route.meta.seo;
        }
      }
      const title = seo.title || this.$gettext("OGOR. Monitoring farm fields with satellites.");
      const description =
        seo.description ||
        this.$gettext(
          "Up to date history on every field in your farm, auto-generated using satellite images. See the crop evolution, fertility areas and productivity on different parcels."
        );
      const prependHost = (s) => (s ? envConfig.VUE_APP_HOST + s : null);
      const image = prependHost(seo.image) || prependHost("/ogor_social.jpg");
      const metas = [
        // Basic app
        {
          name: "application-name",
          content: title,
          id: "app-name",
        },
        {
          name: "description",
          content: description,
          id: "app-desc",
        },
        // Facebook / OpenGraph
        {
          property: "og:title",
          content: title,
          id: "og-app-title",
        },
        {
          property: "og:description",
          content: description,
          id: "og-app-desc",
        },
        {
          property: "og:image",
          content: image,
          id: "og-app-image",
        },
        // Twitter
        {
          name: "twitter:title",
          content: title,
          id: "og-twitter-title",
        },
        {
          name: "twitter:description",
          content: description,
          id: "og-twitter-desc",
        },
        {
          property: "twitter:image",
          content: image,
          id: "twitter-app-image",
        },
        {
          property: "twitter:card",
          content: "summary_large_image",
          id: "twitter-app-card",
        },
        // G+
        { itemprop: "name", content: title, id: "g-app-title" },
        { itemprop: "description", content: description, id: "g-app-desc" },
      ];
      if (Object.keys(seo).length > 0) {
        const url = envConfig.VUE_APP_HOST + this.$route.path;
        metas.push({
          property: "og:url",
          content: url,
          id: "og-url",
        });
      }
      return metas;
    },
  },
})
export default class App extends mixins(MixedVue, StoreGetters) {
  static components: {
    AppSidebarTransition: typeof AppSidebarTransition;
    SearchSidebar: typeof SearchSidebar;
    PayWall: typeof PayWall;
    DemoFarmModal: typeof DemoFarmModal;
    HijackNotification: typeof HijackNotification;
    ErrorPage: typeof ErrorPage;
    PolicyUpdatePopup: typeof PolicyUpdatePopup;
    CookieConsent: typeof CookieConsent;
    ChangeParcelCrop: typeof ChangeParcelCrop;
    OgorNotifications: typeof OgorNotifications;
    PwaInstallPrompt: typeof PwaInstallPrompt;
    LastSeasonYieldNoticePrompt: typeof LastSeasonYieldNoticePrompt;
    DowntimeNotice: typeof DowntimeNotice;
    DialogManager: typeof DialogManager;
  };

  online = true;
  emailWarned = false;
  errorCode: number = null;
  dataLoaded = false;
  showChangeParcelCrop = false;
  showJournalDialog = false;
  showSetCropsModal = false;
  defferedInstallPrompt: BeforeInstallPromptEvent = null;
  // temporary -- for the set crops modal
  selectedParcelIds: string[] = [];
  // Prompt management
  pwaPromptDismissed = false;
  yieldNoticeDismissed = false;

  get isLicenseSuspended() {
    return this.userLicense && this.userLicense.status === "stop";
  }

  get showDemoFarmPopup() {
    return (
      !this.isLicenseSuspended && !this.user.is_staff && this.isDemoFarm && (hasProperty(this.$route.params, "farmId") || this.$route.name === "demo-farm")
    );
  }

  get showPaywallModal() {
    return (
      this.userEmail &&
      this.userLicense &&
      this.userLatestLegalConsent &&
      (!this.userLicenseIsActive ||
        this.userLicenseIsTrial ||
        (this.userLicense.card_payment && this.userLicense.card_payment.payment_status !== "succeeded")) &&
      !this.$route.meta.noPaywall
    );
  }

  get showPwaPrompt() {
    return this.defferedInstallPrompt && this.userEmail && !this.pwaPromptDismissed;
  }

  get showYieldNoticePrompt() {
    const pwaPromptNotAvailable = !this.defferedInstallPrompt || !this.userEmail;
    return (this.pwaPromptDismissed || pwaPromptNotAvailable) && !this.yieldNoticeDismissed;
  }

  @Watch("$language.current")
  languageChanged() {
    document.querySelector("html").setAttribute("lang", this.$language.current);
    this.setSentryData();
  }

  @Watch("userEmail")
  userEmailChanged(toEmail: string) {
    this.setSentryData();
    if (!toEmail) {
      // @todo - what does this do? check new account or change account address flow...
      this.$router.push({ name: "redirect" });
    } else {
    }
  }

  @Watch("userLatestLegalConsent")
  userLatestLegalConsentChanged() {
    if (envConfig.VUE_APP_ENABLE_TRACKERS !== "true") {
      return;
    }
    if (this.userLatestLegalConsent) {
      let todayDate = new Date().toDateString();
      let cookieMaxAge = 15 * 365 * 24 * 60 * 60; // 15 years
      document.cookie = `cookie_consent=YES+${todayDate}; max-age=${cookieMaxAge}; path=/`;
    }
  }

  /**
   * Watch the route and update the state according to it, with this neat trick
   * we create a link between the URL and the application state.
   */
  @Watch("$route")
  async routeChanged(toRoute: Route, fromRoute: Route) {
    if (!fromRoute.name) {
      this.initPreferredLanguage();
    }
    // Vue triggers this watcher on the initial load of app.
    // If the initial data load has not finished yet, a call to update the
    // state will only duplicate a lot of the calls.
    if (this.dataLoaded) {
      await this.safeSetStateFromRoute();
    }
  }

  created() {
    this.initSentry();
    this.initEventHandlers();
    this.initData().finally(() => {
      // Only show the router view after the attempt to load the data has finished.
      // This prevents views from attempting to load into an invalid state, such as
      // no current farm, or no farms available.
      this.dataLoaded = true;
    });
  }

  mounted() {
    this.initInstallListerer();
    this.addResizeListener();
    this.checkPwaPromptDismissal();
  }

  addResizeListener() {
    this.$store.commit("setIsMobile", isMobile());
    window.addEventListener("resize", () => {
      this.$store.commit("setIsMobile", isMobile());
    });
  }

  async onOnline() {
    if (this.online === false) {
      await this.initData();
    }
    this.online = true;
  }

  onConnectError() {
    this.online = navigator.onLine;
  }

  getNewestSeason() {
    if (!this.seasons || this.totalSeasons <= 0) return;
    return Object.values(this.seasons).reduce((previousValue, currentValue) =>
      currentValue.start_date > previousValue.start_date ? currentValue : previousValue
    );
  }

  /**
   * Initialize the user's preferred language. We try, in order:
   *
   *  - stored preference in the browser
   *  - lang from router
   *  - browser language
   *  - English
   */
  initPreferredLanguage() {
    let storedPreference: string;
    try {
      storedPreference = window.localStorage.getItem("language");
    } catch (e) {
      // User doesn't seem to allow storage
    }

    // Enforce language Romanian if the browser has language English
    let browserLanguage = window.navigator.language.slice(0, 2);
    if (browserLanguage === "en") {
      browserLanguage = "ro";
    }

    let preferredLanguage =
      this.user.preferred_language ||
      storedPreference ||
      this.$route.meta.lang ||
      this.$route.params.lang ||
      this.$route.query.lang ||
      envConfig.VUE_APP_DEFAULT_LANGUAGE ||
      browserLanguage ||
      "ro";
    preferredLanguage = preferredLanguage.toLowerCase().slice(0, 2);
    if (!this.$language.available || !this.$language.available[preferredLanguage]) {
      preferredLanguage = "ro";
    }
    // This was not a manually selected change, so no need to keep it as an user preference.
    // Otherwise this will also be incorrectly set in automated tests and page prerendering.
    setLanguage(preferredLanguage, this, false);
  }

  /**
   * Initialize the data when the application is first loaded. Resolves after
   * all the data has been successfully loaded.
   */
  async initData() {
    await this.$store.dispatch("getInitialData");
    return await this.safeSetStateFromRoute();
  }

  async safeSetStateFromRoute() {
    try {
      return await this.setStateFromRoute();
    } catch (e) {
      switch (e.statusCode) {
        case 404:
          this.notify({
            type: "warning",
            text: this.$gettext("Unable to find the requested data."),
          });
          break;
        case 403:
        case 401:
          if (this.userEmail) {
            this.errorCode = 403;
          } else {
            await this.$store.dispatch("clearState");
          }
          return false;
        default:
          throw e;
      }
    }
    return true;
  }

  /**
   * Check the current route parameters and load the data according:
   *
   *  - initial global data
   *  - selected farm
   *  - selected season
   *  - selected date
   *  - selected parcel
   */
  async setStateFromRoute() {
    if (!this.$route.name || !this.$route.name.includes("map")) {
      this.closeSearchSidebar();
    }
    if (this.$route.query.u) {
      this.$store.commit("setLinkEmail", this.$route.query.u);
    }

    if (this.$route.meta.requireStaff && !this.user.is_staff) {
      this.errorCode = 403;
      return true;
    }

    if (await this.maybeRedirect()) return true;

    const meta = getRouteMeta(this.$route);

    if (meta.public || !this.$route.name) {
      // Don't load anything when viewing public pages.
      return true;
    }

    await this.loadFarmData();
    await this.loadSeasonAndDates();
    await this.loadParcelData();
    await this.loadRasterData();
    if (this.userIsAuthenticated) {
      await this.$store.dispatch("getNotesForCurrentFarm");
    }

    if (meta.requireFullAccess && !this.userHasFullAccessToFarm) {
      this.errorCode = 403;
      return true;
    }

    return true;
  }

  async loadRasterData() {
    const rasterKind = this.$route.query.rasterKind as RasterKind;
    if (!rasterKind || !RASTER_TYPES.has(rasterKind)) {
      this.$store.commit("setRasterKind", DEFAULT_RASTER_KIND);
    } else {
      this.$store.commit("setRasterKind", rasterKind);
    }
  }

  async loadFarmData() {
    const farmId =
      this.$route.params.farmId ||
      (this.$route.name === "demo-farm" && this.demoFarmId) ||
      this.currentFarmId ||
      (this.defaultUserFarm && this.defaultUserFarm.id);

    try {
      if (!this.allFarms[farmId]) {
        // Either user is not logged in, or is logged in and is viewing:
        //  - a shared farm via a public link (so it's not in their list)
        //  - a shared parcel and the user doesn't have access to the whole farm
        await this.$store.dispatch("getSingleSharedFarm", farmId);
      }
      // TODO: this will also attempt to get seasons and dates, however that's not available
      // TODO: in case the user has access to a single parcel in the farm
      await this.$store.dispatch("setCurrentFarm", farmId);
    } catch (e) {
      if (e.statusCode !== 403) throw e;

      if (this.$route.params.parcelId) {
        // The user might just have access to a single parcel, so this farm API
        // is not available.
        this.$store.commit("setCurrentFarmId", farmId);
        return;
      }

      // The farm's owner license may have expired. Try to fall back to
      // another farm that still works.
      const fallbackFarm = this.farmsWithParcels.find((f) => f.id !== farmId && !f.licenseExpired);
      if (fallbackFarm) {
        this.notify({
          type: "warning",
          text: this.$gettext("The selected farm is no longer accessible. Switching to another farm."),
        });
        await this.$router.replace({
          name: "map",
          params: { farmId: fallbackFarm.id },
        });
        return;
      }

      throw e;
    }
  }

  async loadSeasonAndDates() {
    const seasonIdParam = this.$route.params.seasonId;
    const dateRoute = parseDate(this.$route.params.date);
    const seasonRoute = this.seasons[seasonIdParam];
    const newestSeason = this.getNewestSeason();
    let date: Date;

    if (!this.userHasAccessToFarm) {
      await this.$store.dispatch("getSeasonDetails", seasonIdParam || newestSeason.id);
      this.$store.commit("setCurrentSeasonId", seasonIdParam || newestSeason.id);
      if (dateRoute) {
        this.$store.commit("setSelectedDate", dateRoute);
      } else if (this.currentSeason && this.currentSeason.end_date) {
        this.$store.commit("setSelectedDate", this.currentSeason.end_date);
      }
    } else {
      date =
        // Use route data
        dateRoute ||
        // Default to hardcoded value for demo farm
        (seasonRoute && this.isDemoFarm && seasonRoute.id === this.defaultDemoSeasonId && demoDefaultSensingDate) ||
        (!seasonRoute && this.isDemoFarm && demoDefaultSensingDate) ||
        // Use the last date of the season
        firstAvailableDate(seasonRoute, this.availableDates) ||
        (seasonRoute && seasonRoute.end_date) ||
        // Use the last date available from the latest season
        firstAvailableDate(this.getNewestSeason(), this.availableDates) ||
        // Panic default
        this.availableDates[0] ||
        getToday();
      await this.$store.dispatch("setSelectedDateAndSeason", {
        date,
        seasonId: seasonIdParam,
      });
    }
  }

  async loadParcelData() {
    const parcelIdParam = this.$route.params.parcelId;
    const intervalIdParam = this.$route.params.intervalId;
    if (parcelIdParam) {
      await this.$store.dispatch("setCurrentParcel", parcelIdParam);
      this.$store.commit("setSelectedParcelCropIntervalId", intervalIdParam);
    } else {
      await this.$store.dispatch("setCurrentParcel", null);
      this.$store.commit("setSelectedParcelCropIntervalId", null);
    }
  }

  async maybeRedirect() {
    const meta = getRouteMeta(this.$route);

    if (this.$route.name === "redirect" || meta.noRedirect) {
      return false;
    }

    // License expired, don't load anything else because the API
    // will deny access. Redirect the user to an appropriate page
    // that doesn't require authentication. The paywall will
    // automatically show up.
    if (this.userLicense && !this.userLicenseIsActive && meta.requireAuth) {
      await this.$router.push({ name: "redirect" });
      return true;
    }
  }

  /**
   * Initialize various event handlers from teh global event bus.
   */
  initEventHandlers() {
    EventBus.$on(EventBus.events.requestStarted, () => {
      this.$store.dispatch("startProgress", this);
    });
    EventBus.$on(EventBus.events.requestFinished, () => {
      this.$store.dispatch("finishProgress", this);
    });
    EventBus.$on(EventBus.events.apiUnknownError, () => {
      this.notify({
        type: "error",
        text: this.$gettext("Server returned an unknown error. Please try again later."),
      });
    });
    EventBus.$on(EventBus.events.apiReadError, () => {
      this.notify({
        type: "error",
        text: this.$gettext("Unable to read data from to server. Please try again later."),
      });
    });
    EventBus.$on(EventBus.events.apiConnectError, () => {
      this.notify({
        type: "error",
        text: this.$gettext("Unable to connect to server. Please try again later."),
      });
    });
    EventBus.$on(EventBus.events.apiConnectError, this.onConnectError);
    EventBus.$on(EventBus.events.showChangeParcelCrop, () => {
      this.showChangeParcelCrop = true;
    });
    EventBus.$on(EventBus.events.showJournalDialog, () => {
      this.showJournalDialog = true;
    });
    EventBus.$on("set-crops-modal", (value: boolean, selectedParcelIds: string[]) => {
      this.showSetCropsModal = value;
      this.selectedParcelIds = selectedParcelIds;
    });
  }

  async initSentry() {
    if (!useSentry) return;

    const sentryOptions: any = {
      dsn: envConfig.VUE_APP_SENTRY_DSN,
      blacklistUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
      ],
    };
    if (envConfig.VUE_APP_SENTRY_ENV) {
      sentryOptions.environment = envConfig.VUE_APP_SENTRY_ENV;
    }
    if (envConfig.VUE_APP_SENTRY_RELEASE) {
      sentryOptions.release = envConfig.VUE_APP_SENTRY_RELEASE;
    } else if (envConfig.VUE_APP_VERSION) {
      sentryOptions.release = envConfig.VUE_APP_VERSION;
    }
    (await getSentry()).init(sentryOptions);
    await this.setSentryData();
  }

  async setSentryData() {
    if (!useSentry) return;
    const Sentry = await getSentry();

    this.$nextTick(() => {
      Sentry.configureScope((scope) => {
        scope.setUser({ email: this.userEmail ? this.userEmail : undefined });
      });
      Sentry.setTag("page_locale", this.$language.current);
    });
  }

  initInstallListerer() {
    window.addEventListener("beforeinstallprompt", (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      this.defferedInstallPrompt = e;
    });
  }

  onPwaPromptDismissed() {
    this.pwaPromptDismissed = true;
  }

  onYieldNoticeDismissed() {
    this.yieldNoticeDismissed = true;
  }

  checkPwaPromptDismissal() {
    const lastShownDate = parseDate(getFeatureData("pwaInstall").lastShownDate);
    const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
    if (!lastShownDate || Date.now() >= lastShownDate.getTime() + TWO_WEEKS) {
      this.pwaPromptDismissed = false;
    } else {
      this.pwaPromptDismissed = true;
    }
  }

  onCropsChanged(changedParcels: Parcel[]) {
    try {
      changedParcels.forEach((updatedParcel) => {
        const storeParcel = this.$store.getters.parcelIndex[updatedParcel.id];
        if (storeParcel) {
          storeParcel.crop_intervals = updatedParcel.crop_intervals;
        }

        const seasonParcels: any[] = this.$store.getters.parcelsForCurrentSeason;
        const parcelIndex = seasonParcels.findIndex((parcel) => parcel.id === updatedParcel.id);
        if (parcelIndex !== -1) {
          seasonParcels[parcelIndex].crop_intervals = updatedParcel.crop_intervals;
        }
      });

      this.$emit("change");
    } catch (e) {
      console.error(e);
      this.notify({
        type: "error",
        text: this.$gettext("Unable to locally update parcels after crop change."),
      });
    }
  }
}
